/**
 * SeoPage — renderiza qualquer rota programatica de SEO.
 * Mesmo layout editorial da landing (Fraunces + travertino).
 * Injeta JSON-LD schema.org + meta dinamicos via React 19 native metadata
 * (aqui fallback com componente Meta pra React 18).
 */

import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSeoPage } from './seoContent';

function Meta({ title, description }: { title: string; description: string }) {
  useEffect(() => {
    document.title = title;
    const ensure = (sel: string, content: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const attr = sel.match(/name="([^"]+)"/) ?? sel.match(/property="([^"]+)"/);
        if (attr) {
          if (sel.includes('property=')) el.setAttribute('property', attr[1]!);
          else el.setAttribute('name', attr[1]!);
        }
        document.head.appendChild(el);
      }
      el.content = content;
    };
    ensure('meta[name="description"]', description);
    ensure('meta[property="og:title"]', title);
    ensure('meta[property="og:description"]', description);
  }, [title, description]);
  return null;
}

export function SeoPage() {
  const { '*': rest } = useParams();
  const page = getSeoPage(rest ?? '');

  if (!page) {
    return (
      <div className="mx-auto max-w-2xl p-10">
        <h1 className="font-display text-3xl">404</h1>
        <p className="mt-4 text-muted-foreground">Página não encontrada. Volte pra <Link to="/" className="underline">home</Link>.</p>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': page.kind === 'comparison' ? 'Article' : 'WebPage',
    headline: page.h1,
    description: page.metaDescription,
    inLanguage: 'pt-BR',
    author: { '@type': 'Organization', name: 'Pórtico' },
    publisher: {
      '@type': 'Organization',
      name: 'Pórtico',
      logo: { '@type': 'ImageObject', url: 'https://render.prospectar.site/logo.svg' },
    },
    datePublished: '2026-04-21',
    dateModified: '2026-04-21',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: page.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  };

  return (
    <div className="min-h-svh bg-background text-foreground">
      <Meta title={page.title} description={page.metaDescription} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <header className="border-b border-border">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5 md:px-10">
          <Link to="/" className="font-display text-2xl font-semibold text-primary">Pórtico</Link>
          <div className="flex items-center gap-6">
            <Link to="/ltd" className="font-mono text-[11px] uppercase tracking-[0.22em] text-foreground hover:text-primary">LTD 14 dias</Link>
            <Link to={page.ctaHref} className="bg-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-background hover:bg-primary">
              {page.ctaPrimary}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1000px] px-6 py-16 md:px-10 md:py-24">
          <span className="section-no block mb-4">
            § {page.kind === 'comparison' ? 'Comparativo' : page.kind === 'persona' ? 'Solução' : 'Caso de uso'}
          </span>
          <h1 className="font-display text-[clamp(2.2rem,5vw,4rem)] font-light leading-[1.05] tracking-tight">
            {page.h1}
          </h1>
          <p className="mt-8 max-w-3xl text-lg leading-relaxed text-foreground/80">{page.intro}</p>
          <div className="mt-10">
            <Link
              to={page.ctaHref}
              className="inline-flex items-center gap-3 bg-foreground px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] text-background hover:bg-primary hover:text-primary-foreground"
            >
              {page.ctaPrimary} →
            </Link>
          </div>
        </div>
      </section>

      {/* Pain + angle */}
      <section className="border-b border-border bg-secondary/25">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 px-6 py-16 md:grid-cols-2 md:px-10 md:py-24">
          <div>
            <span className="section-no block mb-4">A dor</span>
            <ul className="space-y-4">
              {page.painPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 text-base text-foreground/85">
                  <span className="mt-3 inline-block h-px w-4 shrink-0 bg-primary" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="section-no block mb-4">O que o Pórtico faz</span>
            <ul className="space-y-4">
              {page.ourAngle.map((p) => (
                <li key={p} className="flex items-start gap-3 text-base font-medium text-foreground">
                  <span className="mt-3 inline-block h-px w-4 shrink-0 bg-primary" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Price anchor */}
      {page.priceAnchor && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-[1000px] px-6 py-12 md:px-10 md:py-16">
            <span className="section-no block mb-4">A matemática</span>
            <p className="font-display text-2xl font-light leading-relaxed md:text-3xl">{page.priceAnchor}</p>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1000px] px-6 py-16 md:px-10 md:py-24">
          <span className="section-no block mb-6">Perguntas</span>
          {page.faq.map((f, i) => (
            <details key={i} className="group border-b border-border py-6 open:bg-secondary/30">
              <summary className="flex cursor-pointer list-none items-baseline justify-between gap-6 marker:hidden">
                <div className="flex items-baseline gap-6">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-display text-xl font-medium tracking-tight">{f.q}</span>
                </div>
                <span className="shrink-0 font-display text-2xl font-light text-primary transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-4 max-w-prose pl-10 text-base leading-relaxed text-foreground/80">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA duplo */}
      <section className="border-b border-border bg-sidebar text-sidebar-foreground">
        <div className="mx-auto max-w-[1000px] px-6 py-20 text-center md:px-10 md:py-28">
          <h2 className="font-display text-4xl font-light leading-tight md:text-5xl">
            Teste grátis em <span className="italic text-primary">30 segundos</span>.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-sidebar-foreground/80">
            3 créditos no cadastro. Ou garanta acesso vitalício no LTD — fecha em poucos dias.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link to="/auth/login" className="bg-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-primary-foreground hover:bg-background hover:text-foreground">
              Criar conta grátis
            </Link>
            <Link to="/ltd" className="border border-sidebar-foreground/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-sidebar-foreground hover:bg-sidebar-foreground hover:text-sidebar">
              Ver LTD 14 dias
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-background">
        <div className="mx-auto max-w-[1200px] px-6 py-10 md:px-10 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Pórtico · Render arquitetônico brasileiro · render.prospectar.site
        </div>
      </footer>
    </div>
  );
}
